<template>
  <div ref="rootElement" class="purchase-amount-dropdown group relative cursor-pointer" @mouseover="hovering = true" @mouseleave="hovering = false">
    <input
      ref="inputField"
      v-model="computedModelValue"
      type="number"
      min="1"
      :max="props.max"
      minlength="1"
      :maxlength="props.max.toString().length"
      class="purchase-amount-input h-10 w-14 rounded-md border border-border bg-background p-2 hover:border-border-hover focus:border-border-active focus:outline-none group-hover:border-border-hover"
      @keydown.enter.prevent="closeDropdown()"
      @input="limitInputLength"
      @focus="isDropdownVisible = true"
    />
    <button type="button" class="absolute right-2 top-2.5 size-5" @click="toggleDropdown">
      <Icon class="size-full transition-transform ease-in-out" :class="{ 'rotate-180': isDropdownVisible }" name="chevron-down" />
    </button>
    <ul
      v-show="isDropdownVisible"
      ref="dropdown"
      :style="floatingStyles"
      class="z-10 max-h-64 w-14 list-none overflow-auto rounded-md border border-border-medium bg-background py-2"
    >
      <li v-for="(option, index) in options" :key="index" class="cursor-pointer py-2 text-center hover:text-text-strong" @click="selectOption(option)">{{ option }}</li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import Icon from '~/components/icon/Icon.vue'
import { useFloating, autoUpdate, flip, offset } from '@floating-ui/vue'

const props = withDefaults(
  defineProps<{
    max?: number
    openOnHover?: boolean
    modelValue: number
  }>(),
  {
    max: 999,
    openOnHover: true,
  }
)

const options = ref(Array.from({ length: Math.min(props.max, 30) }, (_, i: number) => i + 1))
const isDropdownVisible = ref<boolean>(false)
const inputField = useTemplateRef('inputField')
const hovering = ref<boolean>(false)
const rootElement = useTemplateRef('rootElement')
const dropdown = useTemplateRef('dropdown')
const { floatingStyles } = useFloating(rootElement, dropdown, {
  whileElementsMounted: autoUpdate,
  middleware: [flip(), offset(4)],
})

const computedModelValue = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

const closeDropdown = () => {
  forceOneIfAmountZero()
  forceMaxIfUserInputExceededAllowedMax()
  isDropdownVisible.value = false
  inputField.value?.blur()
}

const forceOneIfAmountZero = () => {
  const input = inputField.value
  if (input?.value === '0' || input?.value === '') {
    input.value = '1'
    emit('update:modelValue', 1)
  }
}

const forceMaxIfUserInputExceededAllowedMax = () => {
  if (computedModelValue.value > props.max) {
    computedModelValue.value = props.max
  }
}

const toggleDropdown = () => {
  isDropdownVisible.value = !isDropdownVisible.value
  if (isDropdownVisible.value) {
    setTimeout(() => {
      inputField.value?.focus()
    }, 0)
  }
}

const emit = defineEmits<{
  'update:modelValue': [newValue: number]
}>()

const selectOption = (option: number) => {
  computedModelValue.value = option
  isDropdownVisible.value = false
}

const handleClickOutside = useSafeEvent<HTMLElement>((event) => {
  const closest: Element | null = event.target.closest('.purchase-amount-dropdown')
  if (!closest || closest !== rootElement.value) {
    isDropdownVisible.value = false
    forceOneIfAmountZero()
    forceMaxIfUserInputExceededAllowedMax()
  }
})

const limitInputLength = useSafeEvent<HTMLInputElement>((event) => {
  const input = event.target
  if (input.value.length > input.maxLength) {
    input.value = input.value.slice(0, input.maxLength)
  }
  computedModelValue.value = parseInt(event.target.value)
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

watch(hovering, (value) => {
  if (props.openOnHover) {
    if (value === true) {
      inputField.value?.focus()
    } else {
      forceOneIfAmountZero()
      forceMaxIfUserInputExceededAllowedMax()
      inputField.value?.blur()
    }
  }
})
</script>

<style lang="scss">
.purchase-amount-dropdown {
  .purchase-amount-input {
    &[type='number'] {
      appearance: none;
      -moz-appearance: textfield;

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        display: none;
      }
    }
  }

  ul {
    scrollbar-gutter: stable both-edges;
    scrollbar-width: thin;
    z-index: 30;

    &::-webkit-scrollbar {
      width: 11px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #9f9f9f;
      background-clip: padding-box;
      border: 4px solid transparent;
    }

    li {
      transform: translateZ(0);
    }
  }
}
</style>
